import { useRef, useState } from 'react';
import Select from 'react-select';
import Utils, { getSelectStyles } from '../../utils/utils';
import Modal from '../../components/modal';
import AdditionalFieldsModal from '../../components/additional-fields-modal';
import { buildBillableAdditionalFieldsFilter, buildGlAdditionalFieldsFilter } from '../../utils/additional-fields-filter-helpers';
import { expenseUsesDisbursementCode } from '../review-expense/utils/contract-expense-json';
import { createInvoiceLine } from './invoice-review';
import InvoiceLookup from './invoice-lookup';

const Field = ({ id, label, children, wide = false }) => <div className={wide ? 'invoice-review-field invoice-review-field--wide' : 'invoice-review-field'}>
  <label htmlFor={id}>{label}</label>{children}
</div>;

/** Keep one draft per invoice and preserve fields the compact form does not edit. */
export default function InvoiceFields({ json, onChange, data, portal, disabled = false }) {
  const [detailsId, setDetailsId] = useState(null);
  const t = data.t;
  const supplier = json.ListSupplier?.[0];
  const lines = json.ListItem || [];
  const nextViewId = useRef(Math.max(-1, ...lines.map(line => Number(line._view_id) || 0)) + 1);
  const addLine = () => {
    const line = createInvoiceLine(json, data.config?.tax_profile);
    // Never reuse a deleted draft identity: an older tax response may still refer to it.
    line._view_id = Math.max(nextViewId.current, line._view_id);
    nextViewId.current = line._view_id + 1;
    onChange(current => ({ ...current, ListItem: [...current.ListItem, line] }));
  };
  const details = lines.find(line => line._view_id === detailsId);
  const legal = Utils.isEngineType(data, 'legal');
  const update = (key, value) => onChange(current => ({ ...current, [key]: value }));
  const updateLine = (id, fields) => onChange(current => ({ ...current, ListItem: current.ListItem.map(line => line._view_id === id ? { ...line, ...fields } : line) }));
  const chooseSupplier = row => onChange(current => ({
    ...current,
    ListSupplier: row ? [{ Name: row.name, Number: row.number, Index: row._id, GLNatural: row.gl_code || '', CostType: row.cost_type || '' }, ...(current.ListSupplier || []).slice(1)] : [],
    // Supplier defaults may fill blank coding, but must not replace reviewed lines.
    ListItem: current.ListItem.map(line => line.GLNatural || line.CostType || !row?.gl_code ? line : { ...line, GLNatural: row.gl_code }),
  }));
  const money = value => value === null || value === undefined ? '—' : new Intl.NumberFormat(t.lang === 'fr' ? 'fr-CA' : 'en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value));
  return <>
    <div className='invoice-review-fields'>
      <Field id='review-invoice-number' label={t.read('review_invoice.number')}>
        <input id='review-invoice-number' className='form-control' value={json.InvoiceNo || ''} onChange={event => update('InvoiceNo', event.target.value)} required />
      </Field>
      <Field id='review-invoice-date' label={t.read('review_invoice.date')}>
        <input id='review-invoice-date' type='date' className='form-control' value={json.InvoiceDate?.slice(0, 10) || ''} onChange={event => update('InvoiceDate', event.target.value)} required />
      </Field>
      <Field id='review-invoice-supplier' label={t.read('invoice.supplier_name')} wide>
        <InvoiceLookup id='review-invoice-supplier' url='/suppliers/search/' value={supplier?.Name ? { label: [supplier.Name, supplier.Number].filter(Boolean).join(' · '), value: supplier } : null}
          onChange={chooseSupplier} portal={portal} disabled={disabled} t={t} />
      </Field>
    </div>
    <div className='invoice-review-lines-heading'>
      <h3>{t.read('review_invoice.lines')}</h3>
      <button type='button' className='btn btn-outline-primary' onClick={addLine}>{t.read('review_invoice.add_line')}</button>
    </div>
    {lines.map((line, index) => {
      const prefix = `invoice-line-${line._view_id}`;
      const disbursement = expenseUsesDisbursementCode(json, line);
      const code = disbursement ? 'CostType' : 'GLNatural';
      const codes = [...new Set([...(data.config?.tax_profile || []).map(profile => profile.code), line.TaxCode].filter(Boolean))].map(value => ({ value, label: value }));
      return <section className='invoice-review-line' key={line._view_id} aria-label={`${t.read('review_invoice.line')} ${index + 1}`}>
        <div className='invoice-review-line-heading'>
          <h4>{t.read('review_invoice.line')} {index + 1}</h4>
          <button type='button' className='btn btn-link text-danger' aria-label={`${t.read('review_invoice.remove_line')} ${index + 1}`} onClick={() => update('ListItem', lines.filter(item => item._view_id !== line._view_id))}>{t.read('delete')}</button>
        </div>
        <div className='invoice-review-fields'>
          <Field id={`${prefix}-description`} label={t.read('review_invoice.description')} wide>
            <textarea id={`${prefix}-description`} className='form-control' rows={2} value={line.Description || ''} onChange={event => updateLine(line._view_id, { Description: event.target.value })} />
          </Field>
          {(legal || disbursement) && <Field id={`${prefix}-billable`} label={t.read('review_invoice.billable')}>
            <select id={`${prefix}-billable`} className='form-control' value={[true, 1].includes(line.IsBillable) ? '1' : '0'} onChange={event => updateLine(line._view_id, { IsBillable: Number(event.target.value) })}>
              <option value='0'>{t.read('review_invoice.non_billable')}</option><option value='1'>{t.read('review_invoice.billable')}</option>
            </select>
          </Field>}
          <Field id={`${prefix}-coding`} label={t.read(disbursement ? 'invoice.cost_type' : 'invoice.gl_nat')} wide={!disbursement && !legal}>
            <InvoiceLookup id={`${prefix}-coding`} url={disbursement ? '/cost_type/search/' : '/gl/nat/search/'} value={line[code] ? { label: String(line[code]), value: line[code] } : null}
              onChange={row => updateLine(line._view_id, { [code]: row?.name || '' })} portal={portal} disabled={disabled} t={t} />
          </Field>
          {disbursement && <Field id={`${prefix}-matter`} label={t.read('invoice.matter_id')} wide>
            <InvoiceLookup id={`${prefix}-matter`} url='/matters/search/' value={line.MatterId ? { label: line.MatterId, value: line.MatterId } : null}
              onChange={row => updateLine(line._view_id, { MatterId: row?.name || '' })} portal={portal} disabled={disabled} t={t} />
          </Field>}
          <Field id={`${prefix}-tax`} label={t.read('invoice.tax_code')}>
            <Select inputId={`${prefix}-tax`} options={codes} value={codes.find(option => option.value === line.TaxCode) || null}
              onChange={option => updateLine(line._view_id, { TaxCode: option?.value || '' })} isDisabled={disabled}
              classNamePrefix='select' styles={getSelectStyles(false, false)} menuPortalTarget={portal} menuPosition='fixed' />
          </Field>
          <Field id={`${prefix}-amount`} label={t.read('invoice.amount')}>
            <input id={`${prefix}-amount`} type='number' step='0.01' className='form-control' value={line.Total ?? ''} onChange={event => updateLine(line._view_id, { Total: event.target.value })} />
          </Field>
        </div>
        <button type='button' className='btn btn-link invoice-review-details-button' onClick={() => setDetailsId(line._view_id)}>{t.read('invoice.additional_fields')}</button>
      </section>;
    })}
    <dl className='invoice-review-totals' aria-live='polite'>
      <div><dt>{t.read('review_invoice.subtotal')}</dt><dd>{money(json.Total)}</dd></div>
      <div><dt>{t.read('invoice.tax')} 1</dt><dd>{money(json.Tax1)}</dd></div>
      {!data.config?.hide_tax_2_field && <div><dt>{t.read('invoice.tax')} 2</dt><dd>{money(json.Tax2)}</dd></div>}
      <div><dt>{t.read('invoice.total')}</dt><dd>{money(json.TotalWithTax)} {json.Currency || ''}</dd></div>
    </dl>
    {details && <Modal title={t.read('invoice.additional_fields')} width={400} onClose={() => setDetailsId(null)} secondaryAction={() => setDetailsId(null)} secondaryText={t.read('close')}>
      <AdditionalFieldsModal data={data} workItem={details} saving={disabled}
        fields={(expenseUsesDisbursementCode(json, details) ? buildBillableAdditionalFieldsFilter : buildGlAdditionalFieldsFilter)(data, { model: json.Model, isLegalEngine: legal })}
        updateInvoice={(value, path1, path2, viewId, field) => updateLine(viewId, { [field]: value })} />
    </Modal>}
  </>;
}
